import { Search, X } from 'lucide-react';
import { ChangeEvent, FC, KeyboardEvent, useEffect, useState } from 'react';

interface SearchListProps {
  value?: string;
  onSearch: (value: string) => void;
  placeholder?: string;
  title?: string;
  debounceMs?: number;
  size?: number;
  width?: string;
}

const SearchList: FC<SearchListProps> = ({
  value = '',
  onSearch,
  placeholder = 'Buscar...',
  title = 'Buscar en la tabla',
  debounceMs = 500,
  size = 18,
  width = '16rem'
}) => {
  const [text, setText] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  // Mantiene el input sincronizado cuando el valor cambia desde afuera (ej: limpiar filtros).
  // Ignora diferencias de espacios en los bordes: quien guarda el valor recortado no
  // debe borrarle al usuario el espacio que está escribiendo ("juan " -> "juan").
  useEffect(() => {
    setText((current) => (current.trim() === value.trim() ? current : value));
  }, [value]);

  // Busca automáticamente al dejar de escribir
  useEffect(() => {
    if (text.trim() === value.trim()) return;
    const timeout = setTimeout(() => onSearch(text), debounceMs);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && text !== value) {
      onSearch(text);
    }
  };

  const handleClear = () => {
    setText('');
    if (value !== '') onSearch('');
  };

  const isActive = isFocused || text !== '';

  const activeColor = isActive
    ? 'text-secondary-600 dark:text-secondary-300'
    : 'text-inkMuted dark:text-inkMuted-dark';

  return (
    <div
      title={title}
      style={{ width }}
      className="inline-flex h-9 items-center gap-2"
    >
      <Search
        size={size}
        className={
          'shrink-0 transition ' + activeColor + (isActive ? ' scale-110' : '')
        }
      />
      <input
        type="text"
        value={text}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={
          'bg-whiteLight dark:bg-darkLight rounded-full px-2 min-w-0 flex-1 py-1 text-sm outline-none transition placeholder:text-inkMuted dark:placeholder:text-inkMuted-dark ' +
          (isActive
            ? 'border-b  border-secondary-600 text-secondary-600 dark:border-secondary-300 dark:text-secondary-300'
            : 'text-inkMuted dark:text-inkMuted-dark')
        }
      />
      {text !== '' && (
        <button
          type="button"
          title="Limpiar búsqueda"
          onClick={handleClear}
          className="inline-flex shrink-0 items-center justify-center transition hover:scale-110"
        >
          <X size={size - 2} />
        </button>
      )}
    </div>
  );
};

export default SearchList;
